import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { Row } from 'antd';

import {
  connectToAdminSocket,
  disconnectFromAdminSocket,
  fetchBadges,
  fetchClientDealingScenarios,
  fetchCurrencies,
  fetchETFDetails,
  fetchHistoricalStep,
  fetchIndexFunds,
  fetchMarketHours,
  fetchUnderlyingDeposits,
  selectors,
} from 'state';
import Header from './Header';
import Data from './Data';
import Sessions from './Sessions';
import Blueprints from './Blueprints';
import Users from './Users';
import InitialPortfolioNotifications from './InitialPortfolio/InitialPortfolioNotifications';
import InitialPortfolio from './InitialPortfolio';
import Trainers from './Trainers';
import MarketHistory from './MarketHistory';
import MarketExplorer from './MarketExplorer';
import Commands from './Commands';
import Errors from './Errors';
import AdminAI from './AI';
import AIProviders from './AIProviders';
import DevPlans from './DevPlans';
import BondsPlayground from './BondsPlayground';
import SyntheticBondsTab from './SyntheticBondsTab';
import AssessmentsAdmin from './Assessments';
import ScenarioBuilderAdmin from './ScenarioBuilder';
import SkillMapAdmin from './SkillMap';
import AdminAssetsSwitch from '../../features/assetCatalogue/AdminAssetsSwitch';
import TandemControl from '../../features/tandem/TandemControl';

const CatalogueStudio = React.lazy(
  () => import('../../features/assetCatalogue/studio/CatalogueStudio'),
);
const Monitoring = React.lazy(() => import('./Monitoring'));

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectors.token);
  const assetsPage = useRouteMatch('/dashboard/assets');
  const studioPage = useRouteMatch('/dashboard/catalogue-studio');
  // console.log('token before admin dashboard index: ', token);
  useEffect(() => {
    dispatch(fetchBadges({}));
    dispatch(fetchCurrencies({}));
    dispatch(fetchUnderlyingDeposits({}));
    dispatch(fetchIndexFunds({}));
    dispatch(fetchHistoricalStep({}));
    dispatch(fetchMarketHours({}));
    dispatch(fetchClientDealingScenarios({}));
    dispatch(fetchETFDetails({}));
  }, [dispatch]);

  useEffect(() => {
    if (!token) {
      // console.log('RETURN NULL');
      return;
    }

    dispatch(connectToAdminSocket(token));

    return () => {
      dispatch(disconnectFromAdminSocket(token));
    };
  }, [dispatch]);

  return (
    <div>
      <InitialPortfolioNotifications />
      <Header />
      <Row justify="center">
        <div
          className="admin-dashboard-content"
          style={{
            width: '100%',
            padding:
              assetsPage || studioPage
                ? '12px 24px 32px'
                : '30px 70px 70px 70px',
          }}
        >
          <Switch>
            <Route path="/dashboard/monitoring">
              <React.Suspense fallback={<p>Loading monitoring…</p>}>
                <Monitoring />
              </React.Suspense>
            </Route>
            <Route path="/dashboard/tandem-comparisons/:pairId" component={TandemControl} />
            <Route path="/dashboard/catalogue-studio">
              <React.Suspense fallback={<p>Loading catalogue studio…</p>}>
                <CatalogueStudio />
              </React.Suspense>
            </Route>
            <Route path="/dashboard/assets">
              <AdminAssetsSwitch />
            </Route>
            <Route path={'/dashboard/data'}>
              <Data />
            </Route>
            <Route path={'/dashboard/market-history'}>
              <MarketHistory />
            </Route>
            <Route path={'/dashboard/market-explorer'}>
              <MarketExplorer />
            </Route>
            <Route path={'/dashboard/sessions'}>
              <Sessions />
            </Route>
            <Route path={'/dashboard/blueprints'}>
              <Blueprints />
            </Route>
            <Route path={'/dashboard/assessments'}>
              <AssessmentsAdmin />
            </Route>
            <Route path={'/dashboard/scenario-builder'}>
              <ScenarioBuilderAdmin />
            </Route>
            <Route path={'/dashboard/ai-providers'}>
              <AIProviders />
            </Route>
            <Route exact path={'/dashboard/ai'}>
              <AdminAI />
            </Route>
            <Route path={'/dashboard/users'}>
              <Users />
            </Route>
            <Route path={'/dashboard/trainers'}>
              <Trainers />
            </Route>
            <Route path={'/dashboard/initial-portfolio'}>
              <InitialPortfolio />
            </Route>
            <Route path={'/dashboard/commands'}>
              <Commands />
            </Route>
            <Route path={'/dashboard/errors'}>
              <Errors />
            </Route>
            <Route path={'/dashboard/devplans'}>
              <DevPlans />
            </Route>
            <Route path={'/dashboard/skill-map'}>
              <SkillMapAdmin />
            </Route>
            <Route path={'/dashboard/bonds-playground'}>
              <BondsPlayground />
            </Route>
            <Route path={'/dashboard/synthetic-bonds'}>
              <SyntheticBondsTab />
            </Route>
            <Redirect to={'/dashboard/data'} />
          </Switch>
        </div>
      </Row>
    </div>
  );
};

export default AdminDashboard;
